import React from 'react';
import { useTranslation } from 'react-i18next';
import { PeriodPicker } from '@/components/DiaryHeader/PeriodPicker';
import {
  DAYS_IN_WEEK,
  addDays,
  endOfWeek,
  formatWeekRange,
  startOfWeek,
} from '@/utils/date';

interface WeekStepParams {
  date: Date;
  weeks: number;
  from?: Date;
  to?: Date;
}

const getWeekStepDate = ({
  date,
  weeks,
  from,
  to,
}: WeekStepParams): Date | undefined => {
  const steppedDate = addDays(date, weeks * DAYS_IN_WEEK);
  const weekStart = startOfWeek(steppedDate);
  const weekEnd = endOfWeek(steppedDate);

  if ((from && weekEnd < from) || (to && weekStart > to)) {
    return undefined;
  }

  return from && weekStart < from ? from : weekStart;
};

interface Props {
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  hasCalendarAbove?: boolean;
  onChange: (date: Date) => void;
  onCalendarSelect: (date: Date) => void;
}

export const WeekPicker: React.FC<Props> = ({
  value,
  minDate,
  maxDate,
  hasCalendarAbove = false,
  onChange,
  onCalendarSelect,
}) => {
  const { t } = useTranslation();
  const weekStart = startOfWeek(value);
  const weekEnd = endOfWeek(value);
  const previousWeekDate = getWeekStepDate({
    date: value,
    weeks: -1,
    from: minDate,
    to: maxDate,
  });
  const nextWeekDate = getWeekStepDate({
    date: value,
    weeks: 1,
    from: minDate,
    to: maxDate,
  });

  return (
    <PeriodPicker
      label={formatWeekRange(weekStart, weekEnd)}
      previousLabel={t('diary.previousWeek')}
      nextLabel={t('diary.nextWeek')}
      value={value}
      minDate={minDate}
      maxDate={maxDate}
      hasCalendarAbove={hasCalendarAbove}
      onChange={onCalendarSelect}
      onPrevious={previousWeekDate && (() => onChange(previousWeekDate))}
      onNext={nextWeekDate && (() => onChange(nextWeekDate))}
    />
  );
};
